/**
 * 🔒 شاشة حاجزة — الحساب داخل بكلمة سر **مؤقتة**.
 *
 * ⛔ **ليه حاجزة مش تنبيه:** الكلمة المؤقتة بيصدرها المدير و**بيشوفها بعينه**
 *    وبيبعتها في رسالة. لو الموظف عدّاها، بيفضل شغّال طول عمره بكلمة سر
 *    شخص تاني يعرفها ومكتوبة في محادثة. التنبيه اللي ينفع تقفله = مفيش قفل.
 *
 * 🔑 والباك إند بيلغي **كل الجلسات التانية** عند التغيير — يعني جهاز المدير
 *    بيتقفل واللي هنا بيفضل شغّال. ده اللي بيقفل الحلقة فعلًا.
 */
import { useState } from "react";

import { changePassword } from "@/api/auth";
import { errorMessageAr } from "@/api/client";
import { useSession } from "@/lib/session";

const MIN_LENGTH = 8;

export function MustChangePassword() {
  const { me, reload } = useSession();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return; // قفل ضد الدوس المزدوج
    if (!current) return setError("اكتب الكلمة المؤقتة اللي وصلتك");
    if (next.length < MIN_LENGTH) {
      return setError(`كلمة السر الجديدة لازم ${MIN_LENGTH} حروف على الأقل`);
    }
    // ⚠️ التأكيد بيتفحص هنا مش في السيستم — الغلطة المطبعية في كلمة سر
    //    مش هتظهر غير في الدخول الجاي، وساعتها الموظف يبقى مقفول بره.
    if (next !== confirm) return setError("الكلمتان مش متطابقتان");
    if (next === current) return setError("اختار كلمة غير المؤقتة");

    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      // ⚠️ **الجلسة بتنتهي مع التغيير** — متحقَّق منه بتست
      //    (`test_changing_the_password_ends_the_session`). فممنوع نعمل
      //    `reload()` ونسيبها تشوف «الجلسة انتهت» كأنها غلطت. بنقول الحقيقة:
      //    اتغيّرت، سجّلي دخول بيها.
      setDone(true);
    } catch (err) {
      setError(errorMessageAr(err));
      setBusy(false);
    }
  }

  const field = {
    font: "inherit",
    color: "var(--ink)",
    background: "var(--surface)",
    border: "1px solid var(--line)",
    borderRadius: "var(--r-control)",
    padding: "10px 12px",
    width: "100%",
  } as const;

  if (done) {
    return (
      <div
        className="card"
        style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12,
                 maxWidth: 420, width: "100%" }}
      >
        <span className="sec-title">تمام — كلمة السر اتغيّرت ✅</span>
        <span className="t-desc">
          الكلمة المؤقتة بقت لاغية، ومحدش غيرك يعرف الجديدة. سجّلي دخول بيها
          دلوقتي وابدئي شغلك.
        </span>
        <button type="button" className="btn" onClick={() => void reload()}>
          روح لشاشة الدخول
        </button>
      </div>
    );
  }

  return (
    <form
      className="card"
      onSubmit={submit}
      style={{
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 420,
        width: "100%",
      }}
    >
      <span className="sec-title">اختار كلمة سر خاصة بيك</span>
      <span className="t-desc">
        إنت داخل بكلمة سر مؤقتة، والإدارة شايفاها. اختار واحدة تخصّك إنت بس
        عشان تكمّل.
      </span>

      <label className="t-meta" htmlFor="cp-current">
        الكلمة المؤقتة
      </label>
      <input
        id="cp-current"
        type={showPw ? "text" : "password"}
        autoComplete="current-password"
        value={current}
        disabled={busy}
        onChange={(e) => setCurrent(e.target.value)}
        style={field}
      />

      <label className="t-meta" htmlFor="cp-next">
        كلمة السر الجديدة ({MIN_LENGTH} حروف على الأقل)
      </label>
      <input
        id="cp-next"
        type={showPw ? "text" : "password"}
        autoComplete="new-password"
        value={next}
        disabled={busy}
        onChange={(e) => setNext(e.target.value)}
        style={field}
      />

      <label className="t-meta" htmlFor="cp-confirm">
        اكتبها تاني للتأكيد
      </label>
      <input
        id="cp-confirm"
        type={showPw ? "text" : "password"}
        autoComplete="new-password"
        value={confirm}
        disabled={busy}
        onChange={(e) => setConfirm(e.target.value)}
        style={field}
      />

      <label className="t-meta" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={showPw}
          disabled={busy}
          onChange={(e) => setShowPw(e.target.checked)}
        />
        إظهار كلمة السر
      </label>

      {error ? (
        <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}

      <button type="submit" className={`btn${busy ? " is-busy" : ""}`} disabled={busy}>
        {busy ? "بيتغيّر…" : "احفظ وكمّل"}
      </button>

      <span className="t-meta">
        {me?.user?.full_name_ar ? `داخل باسم ${me.user.full_name_ar}` : null}
      </span>
    </form>
  );
}
